import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import './internos.css';
import '../Transferencia/transferencias.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight, faFileAlt } from '@fortawesome/free-solid-svg-icons';
import Sidebar from '../../components/Sidebar';
import Header from '../../components/header';
import Skeleton from '../../components/Skeleton';
import VisibleAccess from '../../permissions/VisibleAccess';
import { useInternals } from '../../context/internalContext';
import { useUnits } from '../../context/unitsContext';
import { usePenalExecution } from '../../context/penalExecutionContext';
import FotoPerfil from '../../assets/avatar.png';
import BinaryImage from '../../components/BinaryImage';
import InternalPhoto from '../../components/InternalPhoto';
import { BINARY_RESOURCES, resolveMainImageRecord, resolveThumbnailField } from '../../utils/binaryFile';

// Grupos com acesso a esta tela simplificada e somente leitura (perfis externos).
// Controla a visibilidade de cada CAMPO/acordeon (não a rota — isso é feito em
// restrictedRoute.js). TODA informação nova (campo ou acordeon) precisa vir com seu
// próprio VisibleAccess, com o array de grupos liberados escrito direto no compareValues
// (sem constante compartilhada) — cada campo pode liberar uma combinação diferente de
// perfis (ex.: só Polícia; Defensoria + Polícia; os três) sem precisar criar/reaproveitar
// uma constante de cenário. Nomes de grupo válidos hoje: 'TJPI', 'POLÍCIA MILITAR /
// POLÍCIA CIVIL', 'DEFENSORIA', 'MPPI' — têm que bater exatamente com o que o backend
// retorna em groups (o próprio VisibleAccess normaliza acento/caixa, mas não corrige
// palavra errada). Perfil externo nunca tem "unidade ativa", então nunca usar
// compareField="unit" em conteúdo desta tela — sempre compareField="groups".
//
// Para um perfil externo NOVO enxergar esta tela (o que aparece aqui não é suficiente,
// é preciso liberar o acesso nos arquivos abaixo também — cada um tem seu próprio
// EXTERNAL_PROFILE_GROUPS, acrescente o grupo ao lado de 'TJPI'/'DEFENSORIA'/'MPPI' em cada um):
// - src/routes/restrictedRoute.js       -> libera a rota /internos e /internos/detalhes-externo/:internalId
// - src/context/usersContext.js (login) -> redireciona pra /internos após o login
// - src/components/Sidebar/index.js     -> mostra o menu mínimo (Internos > Buscar)
// - src/pages/Internos/index.js         -> aponta o ícone de olho pra esta tela em vez da completa

function InternalDetailsExternal() {
  const { getInternalById, listAddressByInternal, listPhone, listFiles, getProcessInternals, listTransfers, getCicles, listInternalImage, getVisitorsByInternalId, openBinaryFile } = useInternals();
  const { getUnitById } = useUnits();
  const { listPenalExecutions } = usePenalExecution();
  const { internalId } = useParams();

  const [internalDetails, setInternalDetails] = useState(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(true);
  const [unitDetails, setUnitDetails] = useState(null);
  const [accordionState, setAccordionState] = useState({
    dadosGerais: true,
    enderecos: false,
    contatos: false,
    documentos: false,
    processos: false,
    historicoTransferencias: false,
    ciclos: false,
    imagens: false,
    visitantes: false,
    faccao: false,
  });

  const [addresses, setAddresses] = useState([]);
  const [isLoadingAddresses, setIsLoadingAddresses] = useState(false);
  const [phones, setPhones] = useState([]);
  const [isLoadingPhones, setIsLoadingPhones] = useState(false);
  const [files, setFiles] = useState([]);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [processesMap, setProcessesMap] = useState({});
  const [processes, setProcesses] = useState([]);
  const [isLoadingProcesses, setIsLoadingProcesses] = useState(false);
  const [penalExecutions, setPenalExecutions] = useState([]);
  const [isLoadingPenalExecutions, setIsLoadingPenalExecutions] = useState(false);
  const [activeProcessTab, setActiveProcessTab] = useState('processes');
  const [internalTransfers, setInternalTransfers] = useState([]);
  const [isLoadingTransfers, setIsLoadingTransfers] = useState(false);
  const [cicles, setCicles] = useState([]);
  const [isLoadingCicles, setIsLoadingCicles] = useState(false);
  const [internalImages, setInternalImages] = useState([]);
  const [isLoadingImages, setIsLoadingImages] = useState(false);
  const [showLargeImage, setShowLargeImage] = useState(false);
  const [largeImageId, setLargeImageId] = useState(null);
  const [largeImageDescription, setLargeImageDescription] = useState('');
  const [largeImageDetailType, setLargeImageDetailType] = useState('');
  const [relatedVisitors, setRelatedVisitors] = useState([]);
  const [isLoadingVisitors, setIsLoadingVisitors] = useState(false);

  const toggleAccordion = (accordion) => {
    setAccordionState((prevState) => ({
      ...prevState,
      [accordion]: !prevState[accordion],
    }));
  };

  const fetchInternalDetails = useCallback(async () => {
    setIsLoadingDetails(true);
    try {
      const internalData = await getInternalById(internalId);
      setInternalDetails(internalData);
    } catch (error) {
      console.error('Erro ao buscar os detalhes do interno:', error);
      setInternalDetails(null);
    } finally {
      setIsLoadingDetails(false);
    }
  }, [internalId, getInternalById]);

  useEffect(() => {
    fetchInternalDetails();
  }, [fetchInternalDetails]);

  // internalDetails.unit_acronym já vem denormalizado no interno, mas nome e cidade da
  // unidade não — é preciso buscar a unidade completa por id (mesmo padrão usado em
  // src/pages/Unidades/unitDetails.js: name e city_name, não city, que é o id da cidade).
  useEffect(() => {
    const fetchUnitDetails = async () => {
      if (!internalDetails?.unit) {
        setUnitDetails(null);
        return;
      }
      const unit = await getUnitById(internalDetails.unit);
      setUnitDetails(unit || null);
    };

    fetchUnitDetails();
  }, [internalDetails?.unit, getUnitById]);

  // Endereços, contatos, documentos e processos não vêm embutidos em getInternalById —
  // cada um tem seu próprio endpoint (mesmo padrão usado na tela completa,
  // src/pages/Internos/internalDetails.js). Busca só na primeira vez que o acordeon abre.
  const fetchAddresses = useCallback(async () => {
    setIsLoadingAddresses(true);
    try {
      const response = await listAddressByInternal(internalId);
      setAddresses(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('Erro ao buscar endereços:', error);
      setAddresses([]);
    } finally {
      setIsLoadingAddresses(false);
    }
  }, [internalId, listAddressByInternal]);

  useEffect(() => {
    if (accordionState.enderecos && internalId) {
      fetchAddresses();
    }
  }, [accordionState.enderecos, internalId, fetchAddresses]);

  const fetchPhones = useCallback(async () => {
    setIsLoadingPhones(true);
    try {
      const response = await listPhone(internalId);
      setPhones(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('Erro ao buscar contatos:', error);
      setPhones([]);
    } finally {
      setIsLoadingPhones(false);
    }
  }, [internalId, listPhone]);

  useEffect(() => {
    if (accordionState.contatos && internalId) {
      fetchPhones();
    }
  }, [accordionState.contatos, internalId, fetchPhones]);

  const fetchFiles = useCallback(async () => {
    setIsLoadingFiles(true);
    try {
      const response = await listFiles(internalId);
      const fileList = Array.isArray(response?.data) ? response.data : (Array.isArray(response) ? response : []);
      setFiles(fileList);

      const hasProcessLinks = fileList.some((file) => file.process !== null && file.process !== undefined);
      if (hasProcessLinks) {
        const processesResponse = await getProcessInternals({ internal: internalId });
        const processList = Array.isArray(processesResponse?.results) ? processesResponse.results : [];
        const map = {};
        processList.forEach((process) => { map[process.id] = process.number; });
        setProcessesMap(map);
      }
    } catch (error) {
      console.error('Erro ao buscar documentos:', error);
      setFiles([]);
    } finally {
      setIsLoadingFiles(false);
    }
  }, [internalId, listFiles, getProcessInternals]);

  useEffect(() => {
    if (accordionState.documentos && internalId) {
      fetchFiles();
    }
  }, [accordionState.documentos, internalId, fetchFiles]);

  const fetchProcesses = useCallback(async () => {
    setIsLoadingProcesses(true);
    try {
      const data = await getProcessInternals({ internal: internalId, ordering: '-created_at', limit: 100 });
      setProcesses(data?.results || []);
    } catch (error) {
      console.error('Erro ao buscar processos:', error);
      setProcesses([]);
    } finally {
      setIsLoadingProcesses(false);
    }
  }, [internalId, getProcessInternals]);

  const fetchPenalExecutions = useCallback(async () => {
    setIsLoadingPenalExecutions(true);
    try {
      const data = await listPenalExecutions({ internal: internalId, limit: 100 });
      setPenalExecutions(data?.results || []);
    } catch (error) {
      console.error('Erro ao buscar execuções penais:', error);
      setPenalExecutions([]);
    } finally {
      setIsLoadingPenalExecutions(false);
    }
  }, [internalId, listPenalExecutions]);

  useEffect(() => {
    if (accordionState.processos && internalId) {
      fetchProcesses();
      fetchPenalExecutions();
    }
  }, [accordionState.processos, internalId, fetchProcesses, fetchPenalExecutions]);

  // Histórico de movimentação entre unidades (exclusivo de DEFENSORIA) e ciclos —
  // entrada/saída do sistema prisional (DEFENSORIA e Polícia Militar/Civil) — mesmo
  // endpoint e campos de src/pages/Internos/internalDetails.js. Grupos liberados em
  // cada VisibleAccess correspondente, mais abaixo no JSX.
  const fetchInternalTransfers = useCallback(async () => {
    setIsLoadingTransfers(true);
    try {
      const response = await listTransfers({ internal: internalId, limit: 50 });
      setInternalTransfers(Array.isArray(response?.results) ? response.results : []);
    } catch (error) {
      console.error('Erro ao buscar histórico de movimentação entre unidades:', error);
      setInternalTransfers([]);
    } finally {
      setIsLoadingTransfers(false);
    }
  }, [internalId, listTransfers]);

  useEffect(() => {
    if (accordionState.historicoTransferencias && internalId) {
      fetchInternalTransfers();
    }
  }, [accordionState.historicoTransferencias, internalId, fetchInternalTransfers]);

  const fetchCicles = useCallback(async () => {
    setIsLoadingCicles(true);
    try {
      const ciclesData = await getCicles({ internal: internalId });
      setCicles(Array.isArray(ciclesData?.results) ? ciclesData.results : []);
    } catch (error) {
      console.error('Erro ao buscar ciclos:', error);
      setCicles([]);
    } finally {
      setIsLoadingCicles(false);
    }
  }, [internalId, getCicles]);

  useEffect(() => {
    if (accordionState.ciclos && internalId) {
      fetchCicles();
    }
  }, [accordionState.ciclos, internalId, fetchCicles]);

  // Galeria de imagens do interno (exclusiva de Polícia) — endpoint dedicado
  // listInternalImage, mesmo usado em src/pages/Internos/internalDetails.js. Diferente
  // da foto principal de "Dados Gerais" (que vem embutida em getInternalById), aqui
  // buscamos a lista completa de imagens anexadas.
  const fetchInternalImages = useCallback(async () => {
    setIsLoadingImages(true);
    try {
      const response = await listInternalImage(internalId);
      const imagesList = Array.isArray(response?.data) ? response.data : (Array.isArray(response) ? response : []);
      setInternalImages(imagesList.filter((image) => image.internal === internalId));
    } catch (error) {
      console.error('Erro ao buscar imagens do interno:', error);
      setInternalImages([]);
    } finally {
      setIsLoadingImages(false);
    }
  }, [internalId, listInternalImage]);

  useEffect(() => {
    if (accordionState.imagens && internalId) {
      fetchInternalImages();
    }
  }, [accordionState.imagens, internalId, fetchInternalImages]);

  const handleThumbnailClick = (imageId, description, detailType) => {
    setLargeImageId(imageId);
    setLargeImageDescription(description);
    setLargeImageDetailType(detailType);
    setShowLargeImage(true);
  };

  const handleCloseLargeImage = () => {
    setShowLargeImage(false);
    setLargeImageId(null);
  };

  // Visitantes vinculados ao interno (exclusivo de Polícia) — mesmo endpoint e campos
  // de src/pages/Internos/internalDetails.js, sem o link "Ver detalhes" pra
  // /detalhes-visitantes/:id (rota fora do alcance do perfil externo).
  const fetchRelatedVisitors = useCallback(async () => {
    setIsLoadingVisitors(true);
    try {
      const visitors = await getVisitorsByInternalId(internalId);
      setRelatedVisitors(Array.isArray(visitors) ? visitors : []);
    } catch (error) {
      console.error('Erro ao buscar visitantes do interno:', error);
      setRelatedVisitors([]);
    } finally {
      setIsLoadingVisitors(false);
    }
  }, [internalId, getVisitorsByInternalId]);

  useEffect(() => {
    if (accordionState.visitantes && internalId) {
      fetchRelatedVisitors();
    }
  }, [accordionState.visitantes, internalId, fetchRelatedVisitors]);

  // A foto principal já vem embutida no retorno de getInternalById (internalDetails.images) —
  // não precisa de uma chamada separada ao endpoint de imagens (esse endpoint só é necessário
  // pra telas com galeria/gestão de múltiplas imagens, que não é o caso aqui).
  // O backend retorna esse campo ora como um único objeto de imagem, ora como lista —
  // o InternalPhoto normaliza os dois formatos e busca a foto pela rota binary.

  // "PIG - PENITENCIÁRIA IRMÃO GUIDO Teresina - PI" (sigla - nome + cidade - estado).
  const unitSummaryLine = [
    [internalDetails?.unit_acronym, unitDetails?.name].filter(Boolean).join(' - '),
    [unitDetails?.city_name, unitDetails?.state_name].filter(Boolean).join(' - '),
  ].filter(Boolean).join(' ');

  const formatCPF = (cpf) => {
    if (!cpf) return '';
    return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const formatDate = (dateString, fallback = '-') => {
    if (!dateString) return fallback;
    const parsedDate = new Date(dateString);
    if (Number.isNaN(parsedDate.getTime())) return fallback;
    return parsedDate.toLocaleDateString('pt-BR');
  };

  const safeArray = (value, fieldName = '') => {
    if (Array.isArray(value)) return value;
    if (value === null || value === undefined) return [];
    if (fieldName) {
      console.warn(`[internalDetailsExternal] Campo esperado como lista: ${fieldName}`, value);
    }
    return [];
  };

  const formatListNames = (items, fallback = 'Nenhum') => {
    if (!Array.isArray(items) || items.length === 0) return fallback;
    return items
      .map((item) => {
        if (typeof item === 'string' || typeof item === 'number') return String(item);
        if (!item || typeof item !== 'object') return null;
        return item.name || item.description || item.title || item.type || item.social_name || (item.id ? `ID ${item.id}` : null);
      })
      .filter(Boolean)
      .join(', ');
  };

  const formatCriminalCapitulationDisplay = (item) => {
    if (typeof item === 'string' || typeof item === 'number') return String(item);
    const article = String(item?.capitulation || item?.art || item?.article || '').trim();
    const typology = (item?.typology || item?.tipology || item?.common_name || item?.tipificacao || item?.typification || '').toUpperCase().trim();
    if (article && typology) return `ART. ${article} - ${typology}`;
    if (article) return `ART. ${article}`;
    return typology;
  };

  const formatTypeVisitors = (typeVisitors) => {
    if (!Array.isArray(typeVisitors) || typeVisitors.length === 0) return '-';
    const labels = typeVisitors.map((item) => item?.name || item?.type || '').filter(Boolean);
    return labels.length ? labels.join(', ') : '-';
  };

  const getRelationshipWithCurrentInternal = (visitor) => {
    const internals = Array.isArray(visitor?.internals) ? visitor.internals : [];
    const currentInternal = internals.find((item) => String(item?.id) === String(internalId));
    return (
      currentInternal?.relationship
      || currentInternal?.type_relationship
      || currentInternal?.type_relationship_name
      || '-'
    );
  };

  const getVisitorEntryAuthorizationLabel = (visitor) => {
    if (visitor?.authorize_entry === true) return 'Autorizado';
    if (visitor?.authorize_entry === false) return 'Não autorizado';
    return '-';
  };

  // Foto do visitante pelo campo visitor.image (registro de visitorimage) — sem chamar o
  // endpoint de imagens por visitante (evita N+1 requisições pra uma tabela com vários visitantes).
  const resolveVisitorImageRecord = (visitor) => resolveMainImageRecord(visitor?.image);

  const formatFactionValue = (value, fallback = '-') => {
    if (!value) return fallback;
    if (typeof value === 'string' || typeof value === 'number') return String(value);
    if (typeof value === 'object') {
      return value.name || value.relationship || value.type || value.title || (value.id ? `ID ${value.id}` : fallback);
    }
    return fallback;
  };

  const getStatusColor = (status) => {
    if (status === 'Preso') return 'red-text';
    if (status === 'Fugitivo') return 'orange-text';
    if (status === 'Liberado') return 'green-text';
    if (status === 'Monitorado') return 'blue-text';
    if (status === 'Saída Temporária' || status === 'Saida Temporaria') return 'purple-text';
    return '';
  };

  const renderAccordionEmptyState = () => (
    <p className="semDados accordion-empty-state">Nenhum dado a ser mostrado por aqui</p>
  );

  if (isLoadingDetails) {
    return (
      <div>
        <Sidebar />
        <Header />
        <div className="container internal-details-page">
          <div className="content open">
            <Skeleton
              type="stack"
              lines={6}
              text="Carregando dados do interno..."
              className="internal-details-skeleton"
              prefix="internal-details-external"
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Sidebar />
      <Header />
      <div className="container internal-details-page">
        <div className={`content ${accordionState.dadosGerais ? 'open' : ''}`}>
          <h4 className="h4-title title-accordion" onClick={() => toggleAccordion('dadosGerais')}>
            Dados Gerais{' '}
            <FontAwesomeIcon
              className="icon-accordion"
              icon={accordionState.dadosGerais ? faChevronRight : faChevronDown}
            />
            {unitSummaryLine ? (
              <VisibleAccess compareField="groups" compareValues={['TJPI', 'POLÍCIA MILITAR / POLÍCIA CIVIL', 'DEFENSORIA', 'MPPI']}>
                <span className="unit-summary-inline">{unitSummaryLine}</span>
              </VisibleAccess>
            ) : null}
          </h4>

          {accordionState.dadosGerais ? (
            internalDetails ? (
              <div className="internal-details">
                <div className="line first-line">
                  <div className="profile-image-container">
                    <VisibleAccess compareField="groups" compareValues={['TJPI', 'POLÍCIA MILITAR / POLÍCIA CIVIL', 'DEFENSORIA', 'MPPI']}>
                      <div className={`status-box status-box-full-width ${getStatusColor(internalDetails.status?.name)}`}>
                        <strong>Status</strong>
                        <p>{internalDetails.status?.name || '-'}</p>
                      </div>
                    </VisibleAccess>
                    <VisibleAccess compareField="groups" compareValues={['TJPI', 'POLÍCIA MILITAR / POLÍCIA CIVIL', 'DEFENSORIA', 'MPPI']}>
                      <div className="profile-image-wrapper">
                        <InternalPhoto
                          internal={internalDetails}
                          alt="Foto interno"
                          className="profile-image"
                        />
                      </div>
                    </VisibleAccess>
                  </div>

                  <div className="basic-info">
                    <VisibleAccess compareField="groups" compareValues={['TJPI', 'POLÍCIA MILITAR / POLÍCIA CIVIL', 'DEFENSORIA', 'MPPI']}>
                      <p><strong>Nome:</strong> {internalDetails.name}</p>
                    </VisibleAccess>
                    <VisibleAccess compareField="groups" compareValues={['POLÍCIA MILITAR / POLÍCIA CIVIL']}>
                      <p><strong>Vulgo:</strong> {formatListNames(safeArray(internalDetails.aka, 'internalDetails.aka'), '-')}</p>
                    </VisibleAccess>
                    <VisibleAccess compareField="groups" compareValues={['TJPI', 'POLÍCIA MILITAR / POLÍCIA CIVIL', 'DEFENSORIA', 'MPPI']}>
                      <p><strong>Nome da Mãe:</strong> {internalDetails.mothers_name}</p>
                    </VisibleAccess>
                    <VisibleAccess compareField="groups" compareValues={['TJPI', 'POLÍCIA MILITAR / POLÍCIA CIVIL', 'DEFENSORIA', 'MPPI']}>
                      <p><strong>CPF:</strong> {formatCPF(internalDetails.cpf)}</p>
                    </VisibleAccess>
                    <VisibleAccess compareField="groups" compareValues={['POLÍCIA MILITAR / POLÍCIA CIVIL']}>
                      <p><strong>Cor/Etnia:</strong> {internalDetails.color_or_ethnicity?.name ?? internalDetails.color_or_ethnicity ?? '-'}</p>
                    </VisibleAccess>
                    <VisibleAccess compareField="groups" compareValues={['POLÍCIA MILITAR / POLÍCIA CIVIL']}>
                      <p><strong>Data de Nascimento:</strong> {formatDate(internalDetails.date_of_birth)}</p>
                    </VisibleAccess>
                  </div>

                  <div className="additional-info">
                    <VisibleAccess compareField="groups" compareValues={['POLÍCIA MILITAR / POLÍCIA CIVIL']}>
                      <p><strong>Identidade:</strong> {internalDetails.rg || '-'}</p>
                    </VisibleAccess>
                    <VisibleAccess compareField="groups" compareValues={['POLÍCIA MILITAR / POLÍCIA CIVIL']}>
                      <p><strong>Naturalidade:</strong> {internalDetails.naturalness_city || '-'}</p>
                    </VisibleAccess>
                    <VisibleAccess compareField="groups" compareValues={['POLÍCIA MILITAR / POLÍCIA CIVIL']}>
                      <p><strong>Nacionalidade:</strong> {internalDetails.nationality_country || '-'}</p>
                    </VisibleAccess>
                    <VisibleAccess compareField="groups" compareValues={['POLÍCIA MILITAR / POLÍCIA CIVIL']}>
                      <p><strong>Estado Civil:</strong> {internalDetails.marital_status?.name ?? internalDetails.marital_status ?? '-'}</p>
                    </VisibleAccess>
                    <VisibleAccess compareField="groups" compareValues={['POLÍCIA MILITAR / POLÍCIA CIVIL']}>
                      <p><strong>Regime:</strong> {internalDetails.regime || '-'}</p>
                    </VisibleAccess>
                    <VisibleAccess compareField="groups" compareValues={['POLÍCIA MILITAR / POLÍCIA CIVIL']}>
                      <p><strong>Natureza da Prisão:</strong> {internalDetails.prison || '-'}</p>
                    </VisibleAccess>
                  </div>
                </div>
              </div>
            ) : (
              <div className="internal-details">
                {renderAccordionEmptyState()}
              </div>
            )
          ) : null}
        </div>

        <VisibleAccess compareField="groups" compareValues={['POLÍCIA MILITAR / POLÍCIA CIVIL']}>
          <div className={`content ${accordionState.enderecos ? 'open' : ''}`}>
            <h4 className="h4-title title-accordion" onClick={() => toggleAccordion('enderecos')}>
              Endereços{' '}
              <FontAwesomeIcon className="icon-accordion" icon={accordionState.enderecos ? faChevronRight : faChevronDown} />
            </h4>
            {accordionState.enderecos ? (
              internalDetails ? (
                <div className="content-table">
                  {isLoadingAddresses ? (
                    renderAccordionEmptyState()
                  ) : addresses.length > 0 ? (
                    <table>
                      <thead>
                        <tr>
                          <th>País</th>
                          <th>Estado</th>
                          <th>Cidade</th>
                          <th>Bairro</th>
                          <th>Principal</th>
                        </tr>
                      </thead>
                      <tbody>
                        {addresses.map((address) => (
                          <tr key={address.id}>
                            <td>{address.country}</td>
                            <td>{address.state}</td>
                            <td>{address.city}</td>
                            <td className="td-limited">{address.neighborhood}</td>
                            <td>{address.main === true ? 'Sim' : 'Não'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    renderAccordionEmptyState()
                  )}
                </div>
              ) : renderAccordionEmptyState()
            ) : null}
          </div>
        </VisibleAccess>

        <VisibleAccess compareField="groups" compareValues={['POLÍCIA MILITAR / POLÍCIA CIVIL']}>
          <div className={`content ${accordionState.contatos ? 'open' : ''}`}>
            <h4 className="h4-title title-accordion" onClick={() => toggleAccordion('contatos')}>
              Contatos{' '}
              <FontAwesomeIcon className="icon-accordion" icon={accordionState.contatos ? faChevronRight : faChevronDown} />
            </h4>
            {accordionState.contatos ? (
              internalDetails ? (
                <div className="content-table">
                  {isLoadingPhones ? (
                    renderAccordionEmptyState()
                  ) : phones.length > 0 ? (
                    <table>
                      <thead>
                        <tr>
                          <th>Nome</th>
                          <th>Título</th>
                          <th>Número</th>
                        </tr>
                      </thead>
                      <tbody>
                        {phones.map((phone) => (
                          <tr key={phone.id}>
                            <td>{phone.name}</td>
                            <td>{phone.title}</td>
                            <td>{phone.number}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    renderAccordionEmptyState()
                  )}
                </div>
              ) : renderAccordionEmptyState()
            ) : null}
          </div>
        </VisibleAccess>

        <VisibleAccess compareField="groups" compareValues={['POLÍCIA MILITAR / POLÍCIA CIVIL']}>
          <div className={`content ${accordionState.documentos ? 'open' : ''}`}>
            <h4 className="h4-title title-accordion" onClick={() => toggleAccordion('documentos')}>
              Documentos{' '}
              <FontAwesomeIcon className="icon-accordion" icon={accordionState.documentos ? faChevronRight : faChevronDown} />
            </h4>
            {accordionState.documentos ? (
              internalDetails ? (
                <div className="content-table">
                  {isLoadingFiles ? (
                    renderAccordionEmptyState()
                  ) : files.length > 0 ? (
                    <table>
                      <thead>
                        <tr>
                          <th>Data</th>
                          <th>Tipo</th>
                          <th>Descrição</th>
                          <th>Processo</th>
                          <th style={{ textAlign: 'center' }}>Visualizar</th>
                        </tr>
                      </thead>
                      <tbody>
                        {files.map((file) => (
                          <tr key={file.id}>
                            <td>{formatDate(file.date)}</td>
                            <td>
                              {safeArray(file.type, 'file.type').length > 0
                                ? safeArray(file.type, 'file.type').map((type, idx) => (
                                  <div key={idx}>{type?.type || type?.name || String(type || '-')}</div>
                                ))
                                : '-'}
                            </td>
                            <td className="doc-description">{file.description || '-'}</td>
                            <td>{file.process ? (processesMap[file.process] || `#${file.process}`) : '-'}</td>
                            <td style={{ textAlign: 'center' }}>
                              <FontAwesomeIcon
                                className="icoFile"
                                icon={faFileAlt}
                                onClick={() => openBinaryFile({ resource: BINARY_RESOURCES.internalFile, id: file.id, field: 'file' })}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    renderAccordionEmptyState()
                  )}
                </div>
              ) : renderAccordionEmptyState()
            ) : null}
          </div>
        </VisibleAccess>

        <VisibleAccess compareField="groups" compareValues={['POLÍCIA MILITAR / POLÍCIA CIVIL', 'DEFENSORIA']}>
          <div className={`content ${accordionState.processos ? 'open' : ''}`}>
            <h4 className="h4-title title-accordion" onClick={() => toggleAccordion('processos')}>
              Processos / Execução Penal{' '}
              <FontAwesomeIcon className="icon-accordion" icon={accordionState.processos ? faChevronRight : faChevronDown} />
            </h4>
            {accordionState.processos ? (
              internalDetails ? (
                <div>
                  <div className="social-tabs">
                    <button
                      type="button"
                      onClick={() => setActiveProcessTab('processes')}
                      className={`social-tab-button ${activeProcessTab === 'processes' ? 'active' : ''}`}
                    >
                      Processos ({processes.length})
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveProcessTab('executions')}
                      className={`social-tab-button ${activeProcessTab === 'executions' ? 'active' : ''}`}
                    >
                      Execuções Penais ({penalExecutions.length})
                    </button>
                  </div>

                  {activeProcessTab === 'processes' && (
                    <div className="content-table">
                      {isLoadingProcesses ? (
                        renderAccordionEmptyState()
                      ) : processes.length > 0 ? (
                        <table className="processes-table">
                          <thead>
                            <tr>
                              <th>Número</th>
                              <th>Regime</th>
                              <th>Execução penal</th>
                              <th className="process-col-capitulation">Capitulação Criminal</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {processes.map((process) => (
                              <tr key={process.id}>
                                <td>{process.number}</td>
                                <td>{process.regime?.name || process.regime?.description || process.regime?.regime || process.regime || '-'}</td>
                                <td>{process.execution ? (penalExecutions.find((execution) => execution.id === process.execution)?.nu || `Exec #${process.execution}`) : '-'}</td>
                                <td className="process-col-capitulation">
                                  {Array.isArray(process.criminal_capitulation) ? (
                                    process.criminal_capitulation.length > 0 ? (
                                      <div className="process-capitulation-cell">
                                        {process.criminal_capitulation.map((item, index) => (
                                          <div className="process-capitulation-item" key={item?.id || index}>
                                            {formatCriminalCapitulationDisplay(item)}
                                          </div>
                                        ))}
                                      </div>
                                    ) : '-'
                                  ) : process.criminal_capitulation ? (
                                    <div className="process-capitulation-cell">
                                      <div className="process-capitulation-item">
                                        {formatCriminalCapitulationDisplay(process.criminal_capitulation)}
                                      </div>
                                    </div>
                                  ) : '-'}
                                </td>
                                <td>
                                  <span className={`process-status-label ${process.is_active !== false ? 'process-status-active' : 'process-status-inactive'}`}>
                                    {process.is_active !== false ? 'Ativo' : 'Inativo'}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        renderAccordionEmptyState()
                      )}
                    </div>
                  )}

                  {activeProcessTab === 'executions' && (
                    <div className="content-table">
                      {isLoadingPenalExecutions ? (
                        renderAccordionEmptyState()
                      ) : penalExecutions.length > 0 ? (
                        <table className="processes-table">
                          <thead>
                            <tr>
                              <th>Número</th>
                              <th>Regime Atual</th>
                              <th>Pena (Anos/Meses/Dias)</th>
                              <th>Ativo</th>
                            </tr>
                          </thead>
                          <tbody>
                            {penalExecutions.map((execution) => (
                              <tr key={execution.id}>
                                <td>{execution.nu}</td>
                                <td>{execution.current_regime_name || execution.current_regime || '-'}</td>
                                <td>{execution.years || 0}a/{execution.months || 0}m/{execution.days || 0}d</td>
                                <td>{execution.is_active ? 'Sim' : 'Não'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        renderAccordionEmptyState()
                      )}
                    </div>
                  )}
                </div>
              ) : renderAccordionEmptyState()
            ) : null}
          </div>
        </VisibleAccess>

        <VisibleAccess compareField="groups" compareValues={['POLÍCIA MILITAR / POLÍCIA CIVIL']}>
          <div className={`content ${accordionState.imagens ? 'open' : ''}`}>
            <h4 className="h4-title title-accordion" onClick={() => toggleAccordion('imagens')}>
              Imagens{' '}
              <FontAwesomeIcon className="icon-accordion" icon={accordionState.imagens ? faChevronRight : faChevronDown} />
            </h4>
            {accordionState.imagens ? (
              internalDetails ? (
                <div className="images-grid">
                  {isLoadingImages ? (
                    renderAccordionEmptyState()
                  ) : internalImages.length > 0 ? (
                    internalImages.map((image) => (
                      <div key={image.id} className="image-thumbnail">
                        <BinaryImage
                          resource={BINARY_RESOURCES.internalImage}
                          id={image.id}
                          field={resolveThumbnailField(image)}
                          alt="Imagem do interno"
                          onClick={() => handleThumbnailClick(image.id, image.description, image.detail_type)}
                        />
                      </div>
                    ))
                  ) : (
                    renderAccordionEmptyState()
                  )}
                </div>
              ) : renderAccordionEmptyState()
            ) : null}
          </div>
        </VisibleAccess>

        <VisibleAccess compareField="groups" compareValues={['POLÍCIA MILITAR / POLÍCIA CIVIL']}>
          <div className={`content ${accordionState.visitantes ? 'open' : ''}`}>
            <h4 className="h4-title title-accordion" onClick={() => toggleAccordion('visitantes')}>
              Visitantes{' '}
              <FontAwesomeIcon className="icon-accordion" icon={accordionState.visitantes ? faChevronRight : faChevronDown} />
            </h4>
            {accordionState.visitantes ? (
              internalDetails ? (
                <div className="content-table">
                  {isLoadingVisitors ? (
                    renderAccordionEmptyState()
                  ) : relatedVisitors.length > 0 ? (
                    <table>
                      <thead>
                        <tr>
                          <th>Foto</th>
                          <th>Nome</th>
                          <th>CPF</th>
                          <th>Tipo de visitante</th>
                          <th>Parentesco</th>
                          <th>Autorização de entrada</th>
                        </tr>
                      </thead>
                      <tbody>
                        {relatedVisitors.map((visitor) => (
                          <tr key={visitor?.id || `${visitor?.cpf || 'visitor'}-${visitor?.name || ''}`}>
                            <td>
                              <BinaryImage
                                resource={BINARY_RESOURCES.visitorImage}
                                id={resolveVisitorImageRecord(visitor)?.id}
                                field={resolveThumbnailField(resolveVisitorImageRecord(visitor))}
                                fallbackSrc={FotoPerfil}
                                alt={`Foto de ${visitor?.name || 'visitante'}`}
                                className="visitor-thumbnail"
                              />
                            </td>
                            <td>{visitor?.name || '-'}</td>
                            <td>{formatCPF(String(visitor?.cpf || '').replace(/\D/g, '')) || '-'}</td>
                            <td>{formatTypeVisitors(visitor?.type_visitor)}</td>
                            <td>{getRelationshipWithCurrentInternal(visitor)}</td>
                            <td>{getVisitorEntryAuthorizationLabel(visitor)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    renderAccordionEmptyState()
                  )}
                </div>
              ) : renderAccordionEmptyState()
            ) : null}
          </div>
        </VisibleAccess>

        <VisibleAccess compareField="groups" compareValues={['POLÍCIA MILITAR / POLÍCIA CIVIL']}>
          <div className={`content ${accordionState.faccao ? 'open' : ''}`}>
            <h4 className="h4-title title-accordion" onClick={() => toggleAccordion('faccao')}>
              Vinculação Organizacional/Facção{' '}
              <FontAwesomeIcon className="icon-accordion" icon={accordionState.faccao ? faChevronRight : faChevronDown} />
            </h4>
            {accordionState.faccao ? (
              internalDetails ? (
                <div className="internal-details">
                  <p><strong>Facção:</strong> {formatFactionValue(internalDetails.faction_relationship?.faction)}</p>
                  <p><strong>Relação:</strong> {formatFactionValue(internalDetails.faction_relationship?.relationship)}</p>
                  <p><strong>Função:</strong> {formatFactionValue(internalDetails.faction_relationship?.function)}</p>
                </div>
              ) : renderAccordionEmptyState()
            ) : null}
          </div>
        </VisibleAccess>

        <VisibleAccess compareField="groups" compareValues={[]}>
          <div className={`content ${accordionState.historicoTransferencias ? 'open' : ''}`}>
            <h4 className="h4-title title-accordion" onClick={() => toggleAccordion('historicoTransferencias')}>
              Histórico de Movimentação entre Unidades{' '}
              <FontAwesomeIcon className="icon-accordion" icon={accordionState.historicoTransferencias ? faChevronRight : faChevronDown} />
            </h4>
            {accordionState.historicoTransferencias ? (
              internalDetails ? (
                <div className="content-table">
                  {isLoadingTransfers ? (
                    renderAccordionEmptyState()
                  ) : internalTransfers.length > 0 ? (
                    <table>
                      <thead>
                        <tr>
                          <th>Unidade de origem</th>
                          <th>Unidade de destino</th>
                          <th>Data de saída</th>
                          <th>Data de entrada</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {internalTransfers.map((transfer) => {
                          let statusLabel = '';
                          let statusClass = '';
                          if ((transfer.is_active === 1 || transfer.is_active === true) && !transfer.received) {
                            statusLabel = 'Pendente'; statusClass = 'status-vermelho';
                          } else if ((transfer.is_active === 0 || transfer.is_active === false) && transfer.received) {
                            statusLabel = 'Realizada'; statusClass = 'status-verde';
                          } else if ((transfer.is_active === 0 || transfer.is_active === false) && !transfer.received && transfer.no_receipt) {
                            statusLabel = 'Recusada'; statusClass = 'status-amarelo';
                          } else if ((transfer.is_active === 0 || transfer.is_active === false) && !transfer.received && !transfer.no_receipt) {
                            statusLabel = 'Cancelada'; statusClass = 'status-azul';
                          }
                          return (
                            <tr key={transfer.id}>
                              <td className="td-limited">{transfer.sender_name || '-'}</td>
                              <td className="td-limited">{transfer.recipient_name || '-'}</td>
                              <td>{formatDate(transfer.date)}</td>
                              <td>{transfer.received ? formatDate(transfer.date_recipient) : '-'}</td>
                              <td>{statusLabel && <span className={statusClass}>{statusLabel}</span>}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  ) : (
                    renderAccordionEmptyState()
                  )}
                </div>
              ) : renderAccordionEmptyState()
            ) : null}
          </div>
        </VisibleAccess>

        <VisibleAccess compareField="groups" compareValues={['DEFENSORIA', 'POLÍCIA MILITAR / POLÍCIA CIVIL']}>
          <div className={`content ${accordionState.ciclos ? 'open' : ''}`}>
            <h4 className="h4-title title-accordion" onClick={() => toggleAccordion('ciclos')}>
              Entrada e Saída do Sistema Prisional{' '}
              <FontAwesomeIcon className="icon-accordion" icon={accordionState.ciclos ? faChevronRight : faChevronDown} />
            </h4>
            {accordionState.ciclos ? (
              internalDetails ? (
                <div className="content-table">
                  {isLoadingCicles ? (
                    renderAccordionEmptyState()
                  ) : cicles.length > 0 ? (
                    <table>
                      <thead>
                        <tr>
                          <th>Regime</th>
                          <th>Em aberto</th>
                          <th>Data inicial</th>
                          <th>Data pausa</th>
                          <th>Data final</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cicles.map((cicle) => (
                          <tr key={cicle.id}>
                            <td>{cicle.regime_name}</td>
                            <td>{cicle.open ? 'Sim' : 'Não'}</td>
                            <td>{cicle.start_date ? formatDate(cicle.start_date) : 'N/A'}</td>
                            <td>{cicle.pause_date ? formatDate(cicle.pause_date) : 'N/A'}</td>
                            <td>{cicle.end_date ? formatDate(cicle.end_date) : 'N/A'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    renderAccordionEmptyState()
                  )}
                </div>
              ) : renderAccordionEmptyState()
            ) : null}
          </div>
        </VisibleAccess>
      </div>

      {showLargeImage ? (
        <div className="large-image-overlay" onClick={handleCloseLargeImage}>
          <BinaryImage resource={BINARY_RESOURCES.internalImage} id={largeImageId} field="picture" alt="Imagem" className="large-image" />
          <div className="large-image-details">
            <p><strong>Detalhe:</strong> {largeImageDetailType}</p>
            <p><strong>Descrição:</strong> {largeImageDescription}</p>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default InternalDetailsExternal;
